/**
 * Desktop tabbed mega menu (2026-09-28).
 *
 * The header renders every columns-type mega menu as "one column per level-2 link, level-3 links listed under it"
 * (sections/header.liquid). On desktop this script turns each of those panels into:
 *   row 2  - a horizontal strip of level-2 tabs (Women / Men / Girls / Boys ...)
 *   panel  - the active tab's level-3 collections as image cards (their square collection image).
 * The original markup is left in place for the mobile drawer and is only hidden on desktop (mega-tabs.css),
 * so nothing changes below the md breakpoint. Menus without level-3 links (TRAVEL, TOYS) get the card grid without a tab row.
 * The first card of every grid is a "view all" card (the group's own square art + the theme's translated "Go to" label).
 * A panel is built on the first hover/focus of its top-level item, so closed menus cost no image downloads.
 * Switch off in the theme editor: Header > "Desktop tabbed mega menu".
 */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 769px)');
  const CAN_HOVER = window.matchMedia('(hover: hover)');
  const HOVER_DELAY = 80;

  /** Swap the width param of a Shopify CDN image URL. */
  const sized = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      return src;
    }
  };

  const el = (tag, className, attrs = {}) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  };

  /** Reads one level-2 <li> of the columns markup into { title, href, image, goLabel, items[] }. */
  const readChild = (li) => {
    const link = li.querySelector(':scope summary a.child-nav__item')
      || li.querySelector(':scope > a.child-nav__item, :scope > div a.child-nav__item');
    if (!link) return null;
    const ownImg = li.querySelector(':scope .main-nav__collection-image img');
    const go = li.querySelector(':scope a.main-nav__item--go');
    const items = [...li.querySelectorAll(':scope .main-nav__grandchild > li > a.grandchild-nav__item')].map((a) => {
      const img = a.querySelector('img');
      return { title: a.textContent.trim(), href: a.getAttribute('href'), image: img ? img.getAttribute('src') : '' };
    });
    return {
      title: link.textContent.trim(),
      href: link.getAttribute('href'),
      image: ownImg ? ownImg.getAttribute('src') : '',
      goLabel: go ? go.textContent.trim() : '',
      items
    };
  };

  const card = (item) => {
    const a = el('a', 'mtabs__card', { href: item.href });
    const media = el('span', 'mtabs__media');
    if (item.image) {
      media.append(el('img', '', {
        src: sized(item.image, 240),
        srcset: `${sized(item.image, 240)} 240w, ${sized(item.image, 360)} 360w`,
        sizes: '150px',
        alt: '',
        loading: 'lazy',
        width: '180',
        height: '180'
      }));
    }
    const label = el('span', 'mtabs__label');
    label.textContent = item.title;
    a.append(media, label);
    return a;
  };

  /** "View all" card: the group's own square art with the theme's translated "Go to <title>" label on it. */
  const allCard = (title, href, image, label) => {
    const a = card({ title: label || title, href, image });
    a.classList.add('mtabs__card--all');
    const media = a.querySelector('.mtabs__media');
    if (!image) media.classList.add('mtabs__media--plain');
    media.append(el('span', 'mtabs__shade'), a.querySelector('.mtabs__label'));  // label sits on the art
    return a;
  };

  /** Translated "Go to {title}" pattern taken from any existing go-to link (sections.header.go_to), no locale key. */
  let goTemplate = null;
  const goLabelFor = (title) => {
    if (goTemplate === null) {
      goTemplate = '';
      const go = document.querySelector('.main-nav a.main-nav__item--go');
      const li = go && go.closest('li.col-start-1') ? go.closest('details') : null;
      const own = li ? li.querySelector(':scope > summary a.child-nav__item') : null;
      if (go && own && go.textContent.includes(own.textContent.trim())) {
        goTemplate = go.textContent.trim().replace(own.textContent.trim(), '{title}');
      }
    }
    return goTemplate ? goTemplate.replace('{title}', title) : title;
  };

  /** Square art of a collection link rendered elsewhere in the menu (e.g. TRAVEL's collection under ACCESSORIES). */
  const artFor = (href) => {
    const path = (u) => { try { return new URL(u, window.location.href).pathname; } catch (e) { return u; } };
    const match = [...document.querySelectorAll('.main-nav a.child-nav__item')].find((a) => path(a.getAttribute('href')) === path(href));
    const li = match ? match.closest('li') : null;
    const img = li ? li.querySelector('.main-nav__collection-image img') : null;
    return img ? img.getAttribute('src') : '';
  };

  let uid = 0;

  const build = (panel) => {
    if (panel.dataset.mtabs || !DESKTOP.matches) return;
    const list = panel.querySelector(':scope > .container > .child-nav');
    if (!list) return;
    const children = [...list.children]
      .filter((li) => !li.classList.contains('md:hidden'))
      .map(readChild)
      .filter(Boolean);
    if (!children.length) return;
    panel.dataset.mtabs = 'done';

    // menus with no level-3 (TRAVEL, TOYS): the same card grid straight away, no tab row
    if (!children.some((c) => c.items.length)) {
      const details = panel.closest('details');
      const top = details ? details.querySelector(':scope > summary a') : null;
      const flat = el('div', 'mtabs mtabs--flat');
      const grid = el('div', 'mtabs__grid');
      if (top) {
        const title = top.textContent.trim();
        grid.append(allCard(title, top.getAttribute('href'), artFor(top.getAttribute('href')), goLabelFor(title)));
      }
      children.forEach((child) => grid.append(card(child)));
      flat.append(grid);
      list.after(flat);
      panel.classList.add('has-mtabs');
      return;
    }

    const root = el('div', 'mtabs');
    const bar = el('div', 'mtabs__bar');
    const panels = el('div', 'mtabs__panels');
    const tabs = [];
    let timer;

    const activate = (index) => {
      tabs.forEach((t, i) => {
        const on = i === index;
        t.tab.classList.toggle('is-active', on);
        t.tab.setAttribute('tabindex', on ? '0' : '-1');
        t.tab.setAttribute('aria-expanded', on ? 'true' : 'false');
        t.pane.classList.toggle('is-active', on);
      });
    };

    children.forEach((child, index) => {
      uid += 1;
      const paneId = `mtabs-pane-${uid}`;
      const tab = el('a', 'mtabs__tab', { href: child.href, 'aria-controls': paneId, 'aria-expanded': 'false' });
      tab.textContent = child.title;

      const pane = el('div', 'mtabs__pane', { id: paneId });
      const grid = el('div', 'mtabs__grid');
      // first card = "view all" of this level-2: its own square art + the translated "Go to <title>" label
      grid.append(allCard(child.title, child.href, child.image, child.goLabel || goLabelFor(child.title)));
      child.items.forEach((item) => grid.append(card(item)));
      pane.append(grid);

      // mouse: switch after a short hover intent; touch: first tap opens the tab, a second tap follows the link
      tab.addEventListener('mouseenter', () => {
        if (!CAN_HOVER.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => activate(index), HOVER_DELAY);
      });
      tab.addEventListener('mouseleave', () => clearTimeout(timer));
      tab.addEventListener('click', (evt) => {
        if (!CAN_HOVER.matches && !tab.classList.contains('is-active')) {
          evt.preventDefault();
          activate(index);
        }
      });
      // keyboard: roving tabindex - arrows move between tabs, Tab goes into the active pane
      tab.addEventListener('keydown', (evt) => {
        const rtl = document.documentElement.dir === 'rtl';
        const keys = { Home: 0, End: tabs.length - 1 };
        keys[rtl ? 'ArrowLeft' : 'ArrowRight'] = (index + 1) % tabs.length;
        keys[rtl ? 'ArrowRight' : 'ArrowLeft'] = (index - 1 + tabs.length) % tabs.length;
        if (!(evt.key in keys)) return;
        evt.preventDefault();
        activate(keys[evt.key]);
        tabs[keys[evt.key]].tab.focus();
      });

      tabs.push({ tab, pane });
      bar.append(tab);
      panels.append(pane);
    });

    activate(0);
    root.append(bar, panels);
    list.after(root);
    panel.classList.add('has-mtabs');
  };

  const bind = () => {
    document.querySelectorAll('.main-nav__child.mega-nav--columns').forEach((panel) => {
      if (panel.dataset.mtabsBound) return;
      panel.dataset.mtabsBound = '1';
      const details = panel.closest('details');
      const trigger = details ? details.querySelector(':scope > summary') : null;
      if (!trigger) return;
      const run = () => build(panel);
      trigger.addEventListener('mouseenter', run);
      trigger.addEventListener('focusin', run);
      trigger.addEventListener('touchstart', run, { passive: true });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
  // theme editor: the header section is re-rendered in place
  document.addEventListener('shopify:section:load', bind);
})();
